/**
 * One table on the board.
 *
 * The card is the shell: header, a scrollable body, and the full-screen view
 * its header opens. The dishes themselves are `TableCardBody`, shared with that
 * full-screen view so the two cannot show a cook different things about the
 * same table.
 */
import { useState } from "react";
import {
  isActiveKitchenItem,
  itemCategoryId,
} from "@shared/kds/table-board";
import type { TableGroup, KdsBoardConfig } from "@shared/kds/table-board";
import ComandaModal from "./ComandaModal";
import DishDetailModal from "./DishDetailModal";
import ScrollPane from "./ScrollPane";
import TableCardBody from "./TableCardBody";
import TableCardHeader from "./TableCardHeader";
import { useKitchenStore, WAVE_CHANGE_BADGE_MS } from "../stores/kitchen.store";

interface TableCardProps {
  group: TableGroup;
  config: KdsBoardConfig;
  /** The globally oldest active dish, badged on the board. */
  oldestItemId?: string | null;
  /** Ticking clock from the board — one timer for every card. */
  now: number;
  /** Single-station boards (the bar) have no waves to group by. */
  flat?: boolean;
  /**
   * Every group currently on the board. The full-screen view resolves itself
   * against this on every render, so it shows what the board shows rather than
   * a copy taken when it opened.
   */
  liveGroups: TableGroup[];
}

/** Dishes gone out, kept for the "+N" preview. */
function servedItemsOf(group: TableGroup, config: KdsBoardConfig): any[] {
  return group.items.filter(
    (i: any) =>
      i.status === "SERVED" && config.categoryToBucket[itemCategoryId(i) ?? ""] != null,
  );
}

export default function TableCard({
  group,
  config,
  oldestItemId = null,
  now,
  flat = false,
  liveGroups,
}: TableCardProps) {
  // Per-card and deliberately not remembered: it resets with the table.
  const [showServed, setShowServed] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [detailItem, setDetailItem] = useState<any | null>(null);

  const recentWaveChanges = useKitchenStore((s) => s.recentWaveChanges);
  const waveChange = group.orders
    .map((o: any) => recentWaveChanges[o?.id])
    .filter(
      (e): e is { targetWave: number; at: number } =>
        e != null && now - e.at < WAVE_CHANGE_BADGE_MS,
    )
    .sort((a, b) => b.at - a.at)[0];

  const activeItems = group.items.filter((i) => isActiveKitchenItem(i, config));
  // The card disappears when the kitchen has nothing left to do for this table.
  if (activeItems.length === 0) return null;

  const servedItems = servedItemsOf(group, config);

  return (
    <div
      // As tall as its dishes, capped at the column: the board stacks cards
      // one under the other, so a short table no longer holds a whole column.
      className="bg-kds-surface rounded-xl border border-kds-muted/20 flex flex-col min-h-0 overflow-hidden"
      // The column is exactly as wide as `--kds-card-min`, so the card fills it.
      // The width still follows the chosen type size: a dish line that wraps
      // onto two lines costs more height than a wider card costs width.
    >
      <TableCardHeader
        tableName={group.tableName}
        isTakeaway={group.isTakeaway}
        activeCount={activeItems.length}
        servedCount={servedItems.length}
        showServed={showServed}
        onToggleServed={() => setShowServed((v) => !v)}
        onOpenFull={() => setFullScreen(true)}
        waveChange={waveChange}
      />

      <ScrollPane
        bodyClassName="p-2 space-y-2"
        revision={`${activeItems.length}-${showServed}`}
      >
        <TableCardBody
          activeItems={activeItems}
          servedItems={servedItems}
          showServed={showServed}
          config={config}
          now={now}
          oldestItemId={oldestItemId}
          flat={flat}
          onOpenDish={setDetailItem}
        />
      </ScrollPane>

      {fullScreen && (
        <ComandaModal
          target={{ kind: "table", key: group.key }}
          source={{ groups: liveGroups }}
          config={config}
          now={now}
          flat={flat}
          onClose={() => setFullScreen(false)}
        />
      )}

      {detailItem && (
        <DishDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}
    </div>
  );
}
